import * as React from 'react'
import { cn } from '@/lib/utils'

interface TooltipProviderProps {
  children: React.ReactNode
  delayDuration?: number
}

interface TooltipContextValue {
  delayDuration: number
}

const TooltipContext = React.createContext<TooltipContextValue>({ delayDuration: 300 })

export function TooltipProvider({ children, delayDuration = 300 }: TooltipProviderProps) {
  return (
    <TooltipContext.Provider value={{ delayDuration }}>
      {children}
    </TooltipContext.Provider>
  )
}

interface TooltipProps {
  children: React.ReactNode
}

interface TooltipState {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

const TooltipStateContext = React.createContext<TooltipState | null>(null)

export function Tooltip({ children }: TooltipProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <TooltipStateContext.Provider value={{ isOpen, setIsOpen }}>
      <div className="relative inline-flex">{children}</div>
    </TooltipStateContext.Provider>
  )
}

interface TooltipTriggerProps {
  children: React.ReactNode
  asChild?: boolean
}

export function TooltipTrigger({ children, asChild }: TooltipTriggerProps) {
  const context = React.useContext(TooltipStateContext)
  const { delayDuration } = React.useContext(TooltipContext)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  if (!context) return <>{children}</>

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      context.setIsOpen(true)
    }, delayDuration)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    context.setIsOpen(false)
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<React.HTMLAttributes<HTMLElement>>, {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onFocus: () => context.setIsOpen(true),
      onBlur: () => context.setIsOpen(false),
    })
  }

  return (
    <span
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={() => context.setIsOpen(true)}
      onBlur={() => context.setIsOpen(false)}
    >
      {children}
    </span>
  )
}

interface TooltipContentProps {
  children: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
}

export function TooltipContent({ children, side = 'top', className }: TooltipContentProps) {
  const context = React.useContext(TooltipStateContext)

  if (!context || !context.isOpen) return null

  const sideStyles = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  }

  return (
    <div
      className={cn(
        'absolute z-50 px-3 py-2 text-sm bg-foreground text-background rounded-lg shadow-lg',
        sideStyles[side],
        className,
      )}
    >
      {children}
    </div>
  )
}
